import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { InputCurrency, InputQuantity } from '../../../components/Main'
import OrderCorrectionProductModal from './OrderCorrectionProductModal'
import { InteractorProductItemSearch } from '../../../interactors/Main'
import { t } from 'i18next'
import {
  cleanNumber,
  compareDiffData,
  currencyFormat,
  formatDecimal,
} from '../../../utility/Helpers'
import { confirmationAlert, showAlert } from '../../../utility/SweetAlert'
import FeatherIcon from 'feather-icons-react'
import dollarAllocated from './../../../assets/image/icons/dollar-allocated.svg'
import undo from './../../../assets/image/icons/undo.svg'

const OrderCorrectionItemsSection = ({
  label,
  orderData,
  items,
  setItems,
  totalData,
  setTotalData,
  totalAlreadyPaidAndProcess,
  setIsAllocated,
  finalAllocatedValue,
  isAllocatedAmount = false,
  setIsAllocatedAmount,
}) => {
  const pathSegments = window.location.pathname.split('/')
  const currentPage = pathSegments[3]
  const [productList, setProductList] = useState([])
  const { productSearch } = InteractorProductItemSearch()
  const [showModal, setShowModal] = useState(false)
  const [hasResetShown, setHasResetShown] = useState(true)
  const [pagination, setPagination] = useState()
  const [keyword, setKeyword] = useState('')
  const [payloadItemsSearch, setPayloadItemsSearch] = useState({
    page: 1,
    limit: 10,
    keywords: {
      province_uid: [],
      category_main_uid: null,
      category_major_uid: null,
      category_minor_uid: null,
      store_uid: orderData?.store_uid || '',
      price_range: {
        minimum: null,
        maximum: null,
      },
      status: '',
      rating: null,
      text_search: '',
      sort_by: null,
      source: 'MARKETPLACE-CORRECTION',
    },
  })

  const handleItemsSearchResponse = (response) => {
    if (response?.items) {
      const correctionItemsList = response.items.map((item) => ({
        product_title: item.product_title,
        product_quantity: item.minimum_order_quantity,
        product_minimum_order_quantity: item.minimum_order_quantity,
        product_price: item.product_price,
        product_total_price: item.product_price * item.minimum_order_quantity,
        product_thumbnail: item.product_thumbnail,
        product_type: item.product_type,
        product_uid: item.uid,
        product_unit_type: item.product_unit_type,
      }))
      setProductList(correctionItemsList)
      setPagination(response.pages)
    }
  }

  const handleChangePage = (newPage) => {
    const updatedPayload = { ...payloadItemsSearch, page: newPage }
    setPayloadItemsSearch(updatedPayload)
    productSearch(updatedPayload, handleItemsSearchResponse, () => {})
  }

  const handleResetPagination = () => {
    setPayloadItemsSearch((prev) => ({ ...prev, page: 1 }))
  }

  useEffect(() => {
    if (productList.length === 0 && orderData?.store_uid) {
      setPayloadItemsSearch((prevPayload) => {
        const updatedPayload = {
          ...prevPayload,
          keywords: {
            ...prevPayload.keywords,
            store_uid: orderData.store_uid,
          },
        }
        productSearch(updatedPayload, handleItemsSearchResponse, () => {})
        return updatedPayload
      })
    }
  }, [orderData?.store_uid, productList.length, productSearch])

  const handleChange = (index, key, rawValue) => {
    const updated = [...items]

    const cleanValue = cleanNumber(rawValue)
    updated[index][key] = cleanValue

    if (key === 'product_quantity' || key === 'product_price') {
      const qty = cleanNumber(updated[index].product_quantity)
      const price = cleanNumber(updated[index].product_price)
      updated[index].product_total_price = qty * price
      if (isAllocatedAmount) setIsAllocatedAmount(false)
    }
    const resetAllocated = updated.map((item, i) => {
      const productAmountAllocated = cleanNumber(item.product_amount_allocated)
      if (i !== index) {
        return {
          ...item,
          product_amount_allocated_before: productAmountAllocated,
          product_amount_allocated: 0,
        }
      }

      return {
        ...item,
        is_deleted: 0,
        product_amount_allocated_before: productAmountAllocated,
        product_amount_allocated: 0,
      }
    })

    // calculateAllocatedAmount(updated)

    setItems(resetAllocated)
    const totalOrder = updated
      .filter((item) => item.is_deleted !== 1)
      .reduce((acc, item) => acc + cleanNumber(item.product_total_price), 0)
    // const totalUnapplied = corectionItems.reduce(
    //   (sum, item) => sum + (Number(item.product_total_down_payment_unapplied) || 0),
    //   0,
    // )
    const shippingCost = cleanNumber(totalData.shippingCost)
    const insuranceCost = cleanNumber(totalData.insuranceCost)
    const totalPayment = totalOrder + shippingCost + insuranceCost

    setTotalData({
      ...totalData,
      shippingCost,
      insuranceCost,
      totalOrder,
      totalPayment,
    })
  }

  const handleSubmitSearch = (e) => {
    e.preventDefault()
    const updatedPayload = {
      ...payloadItemsSearch,
      page: 1,
      keywords: { ...payloadItemsSearch.keywords, text_search: keyword },
    }
    setPayloadItemsSearch(updatedPayload)
    productSearch(updatedPayload, handleItemsSearchResponse, () => {})
  }

  const handleShippingChange = (val) => {
    const inputShippingCost = cleanNumber(val?.value || 0)
    const insuranceCost = cleanNumber(totalData.insuranceCost || 0)
    const shippingCost = inputShippingCost
    const totalOrder = items
      .filter((item) => item.is_deleted !== 1)
      .reduce((acc, item) => acc + cleanNumber(item.product_total_price), 0)
    const totalPayment = totalOrder + shippingCost + insuranceCost

    setTotalData({
      ...totalData,
      shippingCost,
      totalOrder,
      totalPayment,
    })
  }

  const handleInsurranceChange = (val) => {
    const inputInsuranceCost = cleanNumber(val?.value || 0)
    const shippingCost = cleanNumber(totalData.shippingCost || 0)
    const insuranceCost = inputInsuranceCost
    const totalOrder = items
      .filter((item) => item.is_deleted !== 1)
      .reduce((acc, item) => acc + cleanNumber(item.product_total_price), 0)
    const totalPayment = totalOrder + insuranceCost + shippingCost
    setTotalData({
      ...totalData,
      insuranceCost,
      totalOrder,
      totalPayment,
    })
  }

  const handleReset = (e) => {
    e.preventDefault()
    setKeyword('')
    const updatedPayload = {
      ...payloadItemsSearch,
      page: 1,
      keywords: { ...payloadItemsSearch.keywords, text_search: '' },
    }
    setIsAllocatedAmount(false)
    setPayloadItemsSearch(updatedPayload)
    productSearch(updatedPayload, handleItemsSearchResponse, () => {})
  }

  const calculateTotals = (items, prevTotal) => {
    const shippingCost = cleanNumber(prevTotal.shippingCost)
    const insuranceCost = cleanNumber(prevTotal.insuranceCost)
    const totalOrder = items
      .filter((item) => item.is_deleted !== 1)
      .reduce((acc, item) => acc + cleanNumber(item.product_total_price), 0)
    const totalPayment = totalOrder + insuranceCost + shippingCost

    return {
      ...prevTotal,
      shippingCost,
      insuranceCost,
      totalOrder,
      totalPayment,
    }
  }

  const resetAllocationWithPatch = (items, targetIndex, patchForTarget) => {
    return items.map((item, i) => {
      const baseAllocatedBefore =
        item.product_amount_allocated === 0
          ? item.product_amount_allocated_original
          : item.product_amount_allocated

      if (i !== targetIndex) {
        return {
          ...item,
          product_amount_allocated_before: baseAllocatedBefore,
          product_amount_allocated: 0,
        }
      }

      // item pada index yang diubah
      return {
        ...item,
        ...patchForTarget(item),
        product_amount_allocated_before: baseAllocatedBefore,
        product_amount_allocated: 0,
      }
    })
  }

  const applyItemChangeWithTotals = (index, patchForTarget) => {
    // setIsAllocatedAmount(false)
    setItems((prevItems) => {
      const resetAllocated = resetAllocationWithPatch(prevItems, index, patchForTarget)

      setTotalData((prevTotal) => calculateTotals(resetAllocated, prevTotal))

      return resetAllocated
    })
  }

  const undoItem = async (index) => {
    const confirm = await confirmationAlert({
      icon: 'warning',
      message: t('message.confirmation_undo_product_correction'),
      title: t('title.are_you_sure'),
    })

    if (!confirm) return

    applyItemChangeWithTotals(index, () => ({
      is_deleted: 0,
      status_item: 'undo',
    }))
  }

  // ==================
  // REMOVE ITEM
  // ==================
  const removeItem = async (index) => {
    const confirm = await confirmationAlert({
      icon: 'warning',
      message: t('message.confirmation_remove_product_correction'),
      title: t('title.are_you_sure'),
    })

    if (!confirm) return
    applyItemChangeWithTotals(index, () => ({
      is_deleted: 1,
      status_item: 'deleted',
    }))
  }

  const checkAllDeleted = () => {
    let checkRemainItem = items.filter((item) => item.is_deleted === 0)

    if (checkRemainItem.length === 1) return true

    return false
  }

  const showWarningDeletedAllProduct = () => {
    showAlert({
      icon: 'warning',
      message: t('message.warning_remove_product_correction'),
      reload: false,
    })
  }

  const handleProduct = (item) => {
    const isFoundAllocated = items.every((i) => i.product_amount_allocated !== 0)

    if (currentPage === 'create' && isFoundAllocated) setIsAllocated(true)

    let transactionItems = orderData?.transaction_items || []
    let isExistingProduct = transactionItems.find((elm) => elm.product_uid === item.product_uid)
    let existingPrice = isExistingProduct ? isExistingProduct.product_price : item.product_price
    let existingQuantity = isExistingProduct
      ? isExistingProduct.product_quantity
      : item.product_quantity

    const isAlreadyExist = items.some((i) => i.product_uid === item.product_uid)

    let updated = []

    if (isAlreadyExist) {
      // Hapus jika sudah ada
      updated = items.filter((i) => i.product_uid !== item.product_uid)
    } else {
      // Tambah jika belum ada
      const newItem = {
        product_title: item.product_title,
        product_quantity: existingQuantity,
        product_quantity_before: existingQuantity,
        product_minimum_order_quantity: item.product_minimum_order_quantity,
        product_price: existingPrice,
        product_price_before: existingPrice,
        product_total_price: existingPrice * existingQuantity,
        product_total_price_before: existingPrice * existingQuantity,
        product_thumbnail: item.product_thumbnail,
        product_type: item.product_type,
        product_currency: item.product_price_currency,
        product_uid: item.product_uid,
        product_status: isExistingProduct ? 'existing' : 'added',
        product_unit_type: item.product_unit_type,
        product_already_paid: 0,
        product_already_paid_before: 0,
        is_deleted: 0,
        status_item: 'added',
        product_amount_allocated: 0,
        product_amount_allocated_original: 0,
        product_amount_allocated_before: 0,
        product_total_down_payment_unapplied: 0,
      }
      updated = [...items, newItem]
    }

    setItems(updated)
    const totalOrder = updated
      .filter((item) => item.is_deleted !== 1)
      .reduce((acc, item) => acc + cleanNumber(item.product_total_price), 0)
    const shippingCost = cleanNumber(totalData.shippingCost)
    const insuranceCost = cleanNumber(totalData.insuranceCost)
    const totalPayment = totalOrder + insuranceCost + shippingCost

    setTotalData({
      ...totalData,
      shippingCost,
      insuranceCost,
      totalOrder,
      totalPayment,
    })
  }

  const activeResetButton = useCallback(() => {
    const shouldHideReset =
      totalData?.shippingCostBefore === totalData?.shippingCost &&
      totalData?.insuranceCostBefore === totalData?.insuranceCost &&
      orderData?.transaction_items?.length === items?.length &&
      !compareDiffData(items, 'product_price_before', 'product_price') &&
      !compareDiffData(items, 'product_quantity_before', 'product_quantity') &&
      !items.find((item) => item.product_status === 'added') &&
      !items.find((item) => item.is_deleted === 1)

    setHasResetShown((prev) => {
      const next = shouldHideReset
      if (prev !== next) return next
      return prev
    })
  }, [totalData, orderData, items])

  useEffect(() => {
    if (orderData?.transaction_items?.length > 0) activeResetButton()
  }, [activeResetButton, orderData])

  const onResetChanges = async () => {
    const confirm = await confirmationAlert({
      icon: 'warning',
      message: t('message.confirmation_reset_product_correction'),
      title: t('title.are_you_sure'),
    })

    if (confirm) {
      let transactionItems = orderData?.transaction_items || []

      const resetItems = transactionItems.map((item) => ({
        product_title: item.product_title,
        product_quantity: item.product_quantity,
        product_quantity_before: item.product_quantity,
        product_minimum_order_quantity: item.product_minimum_order_quantity,
        product_price: item.product_price,
        product_price_before: item.product_price,
        product_total_price: item.product_quantity * item.product_price,
        product_total_price_before: item.product_quantity * item.product_price,
        product_currency: item.product_price_currency,
        product_uid: item.product_uid,
        product_status: 'existing',
        product_thumbnail: item.product_thumbnail,
        product_trx_uid: item.uid,
        product_unit_type: item.product_unit_type,
        product_already_paid:
          item.product_price_billed * item.product_quantity_billed - item.product_pph_billed,
        product_already_paid_before:
          item.product_price_billed * item.product_quantity_billed - item.product_pph_billed,
        is_deleted: 0,
        status_item: 'reset',
        product_amount_allocated: 0,
        product_amount_allocated_original: 0,
        product_amount_allocated_before: 0,
        product_total_down_payment_unapplied:
          item.product_total_down_payment_unapplied - item.product_dp_pph_billed,
      }))
      setItems(resetItems)
      setIsAllocatedAmount(false)

      const shippingCost = cleanNumber(totalData.shippingCostBefore)
      const insuranceCost = cleanNumber(totalData.insuranceCostBefore)
      const totalOrder = resetItems.reduce(
        (acc, item) => acc + cleanNumber(item.product_total_price),
        0,
      )
      const totalPayment = totalOrder + shippingCost + insuranceCost

      setTotalData((prev) => ({
        ...prev,
        shippingCost,
        insuranceCost,
        totalOrder,
        totalPayment,
      }))

      setHasResetShown(false)
    }
  }

  return (
    <div>
      <div className="mb-3 fw-bold">{label}</div>
      <div className="d-flex justify-content-between">
        {currentPage !== 'view' && (
          <button
            className="btn btn-sm btn-primary mb-3 pointer"
            onClick={() => setShowModal(true)}
          >
            + {t('button.add_product')}
          </button>
        )}
        <div className="d-flex g-1 mb-3" style={{ gap: '22px' }}>
          {currentPage === 'create' && finalAllocatedValue > 0 && (
            <div className="d-flex align-items-center" style={{ gap: '22px' }}>
              <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                <img src={dollarAllocated} alt="dollar-allocated" />
                <div>
                  <div style={{ fontSize: '11px' }}>{t('title.allocation_available')}</div>
                  <div className="text-success" style={{ fontSize: '18px', lineHeight: '18px' }}>
                    <strong>Rp {formatDecimal(finalAllocatedValue, 5)}</strong>
                  </div>
                </div>
              </div>
              <div
                className="vertical-separator"
                style={{ height: 'calc(100% - 8px)', width: '1px', backgroundColor: '#afafaf' }}
              ></div>
            </div>
          )}
          {currentPage === 'create' && (
            <button
              className="btn btn-sm btn-danger  pointer"
              onClick={onResetChanges}
              disabled={hasResetShown}
            >
              {t('button.reset_correction')}
            </button>
          )}
        </div>
      </div>
      <div className="card p-3">
        <div className="row g-2 fw-semibold text-muted px-1 mb-2">
          <div className="col-md-3">{t('title.product.product_name')}</div>
          <div className="col-md-2">{t('title.product.product_total')}</div>
          <div className="col-md-2">{t('title.product.product_price')}</div>
          <div className="col-md-2">{t('title.product.price_total')}</div>
          <div className="col-md-2">{t('title.product.item_information')}</div>
          {currentPage !== 'view' && (
            <div className="col-md-1 text-end">{t('title.product.product_action')}</div>
          )}
        </div>

        {items?.length > 0 ? (
          items.map((item, index) => {
            let differencePrice = item.product_total_price - item.product_total_price_before
            const absoluteValue = Math.abs(differencePrice)
            const formattedCurrency = currencyFormat(absoluteValue, 'IDR')
            const formattedDifferencePrice =
              differencePrice > 0
                ? `+ ${formattedCurrency}`
                : differencePrice < 0
                ? `- ${formattedCurrency}`
                : formattedCurrency

            const downPaymentUnapplied =
              finalAllocatedValue > 0 ? 0 : cleanNumber(item.product_total_down_payment_unapplied)
            const pphBilled =
              finalAllocatedValue > 0 ? 0 : cleanNumber(item.product_pph_billed || 0)
            let alreadyPaid =
              finalAllocatedValue > 0 ? 0 : cleanNumber(item.product_already_paid || 0)
            if (item.product_type === 'SERVICE') {
              alreadyPaid =
                finalAllocatedValue > 0
                  ? 0
                  : cleanNumber(item.product_already_paid || 0) + pphBilled
            }
            let remainingAmount =
              cleanNumber(item.product_total_price) - (alreadyPaid + downPaymentUnapplied)
            if (isAllocatedAmount) remainingAmount = cleanNumber(item.product_total_price)

            const allocatedAmount = cleanNumber(item.product_amount_allocated || 0)

            return (
              <div
                key={index}
                className="row g-2 mb-3 px-1 py-3"
                style={{
                  backgroundColor:
                    item.is_deleted && item.product_status === 'existing' ? '#fbc3c3' : '',
                }}
              >
                <div className="col-md-3">
                  <div className="d-flex align-items-start">
                    <img
                      width="62"
                      src={item.product_thumbnail}
                      alt="product-thumbnail"
                      className="rounded mr-3 object-fit-cover"
                    />
                    {/* <input
                    className={`form-control ${
                      item.product_status === 'added' ? 'bg-success text-white' : ''
                    }`}
                    value={item.product_title}
                    onChange={(e) => handleChange(index, 'product_title', e.target.value)}
                    readOnly
                  /> */}
                    <div>
                      <div
                        className={`product-title-ellipsis ${
                          item.product_status === 'added'
                            ? 'bg-success w-100 px-3 py-2 text-white rounded-pill '
                            : ''
                        }`}
                        title={item.product_title}
                      >
                        {item.product_title}
                      </div>

                      {currentPage !== 'view' && (
                        <div className="before-value-text">
                          {t('title.minimum_buy')} : {item.product_minimum_order_quantity}{' '}
                          {item.product_unit_type}
                        </div>
                      )}
                    </div>
                  </div>
                  {item.product_status === 'added' && (
                    <div className="before-value-text">{t('title.new_product')}</div>
                  )}
                </div>
                <div className="col-md-2">
                  <InputQuantity
                    id={`item_quantity-${index}`}
                    value={item.product_quantity}
                    onChange={(val) => handleChange(index, 'product_quantity', val.value)}
                    decimal={2}
                    readOnly={currentPage === 'view' || item.is_deleted ? true : false}
                    zeroMinValue={true}
                  />
                  {currentPage !== 'view' && item.product_status !== 'added' && (
                    <div className="before-value-text">
                      {t('form.correction_quantity_before')}:
                      {formatDecimal(cleanNumber(item.product_quantity_before), 2)}
                    </div>
                  )}
                </div>
                <div className="col-md-2">
                  <InputCurrency
                    id={`item_price-${index}`}
                    value={item.product_price}
                    onChange={(val) => handleChange(index, 'product_price', val.value)}
                    decimal={5}
                    currency="IDR"
                    zeroMinValue={true}
                    readOnly={
                      currentPage === 'view' || item.is_deleted || item.product_status !== 'added'
                        ? true
                        : false
                    }
                  />
                  {currentPage !== 'view' && item.product_status !== 'added' && (
                    <div className="before-value-text">
                      {t('form.correction_price_before')}: Rp{' '}
                      {formatDecimal(cleanNumber(item.product_price_before), 5)}
                    </div>
                  )}
                </div>
                <div className="col-md-2">
                  <InputCurrency
                    id={`item_total-${index}`}
                    value={item.product_total_price}
                    decimal={5}
                    readOnly
                    currency="IDR"
                  />
                  {currentPage !== 'view' && item.product_status !== 'added' && (
                    <>
                      <div className="before-value-text">
                        {t('form.correction_total_price_before')}: Rp
                        {formatDecimal(cleanNumber(item.product_total_price_before), 5)}
                      </div>
                      <div
                        className={`before-value-text ${
                          differencePrice > 0
                            ? `text-success`
                            : differencePrice < 0
                            ? `text-danger`
                            : ''
                        }`}
                      >
                        {t('title.product.price_difference')}:{' '}
                        <strong>{formattedDifferencePrice}</strong>
                      </div>
                    </>
                  )}
                </div>
                <div className="col-md-2">
                  <div
                  // className={`py-2 px-3 ${

                  // }`}
                  >
                    <div className="before-value-text mb-1 text-success">
                      {t('title.nominal_billed')}:{' '}
                      <strong>Rp {isAllocatedAmount ? 0 : formatDecimal(alreadyPaid, 2)}</strong>
                    </div>
                    <div className="before-value-text mb-1 text-success">
                      {t('title.dp_billed')}:{' '}
                      <strong>
                        Rp{' '}
                        {isAllocatedAmount
                          ? 0
                          : downPaymentUnapplied > 0
                          ? formatDecimal(downPaymentUnapplied, 2)
                          : 0}
                      </strong>
                    </div>
                    <div className="before-value-text mb-1 text-danger">
                      {t('title.remain_bill')}:{' '}
                      <strong>
                        Rp {remainingAmount > 0 ? formatDecimal(remainingAmount, 2) : 0}
                      </strong>
                    </div>
                    <div className="before-value-text mt-2">
                      {t('title.allocation_dp_amount')}:{' '}
                      <strong>
                        Rp {allocatedAmount > 0 ? formatDecimal(allocatedAmount, 2) : 0}
                      </strong>
                    </div>
                  </div>
                </div>
                {currentPage !== 'view' && (
                  <div className="col-md-1 text-end">
                    {item.is_deleted ? (
                      <div onClick={() => undoItem(index)} className="pointer mt-1">
                        <img src={undo} alt="undo" />
                      </div>
                    ) : (
                      <div
                        onClick={() => {
                          checkAllDeleted() ? showWarningDeletedAllProduct() : removeItem(index)
                        }}
                        className="pointer mt-1"
                      >
                        <FeatherIcon
                          className="trash-2 mb-0"
                          icon="trash-2"
                          size="20"
                          stroke="red"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })
        ) : (
          <div className="row g-2 mb-3 align-items-center px-1">
            <div className="col-md-12 text-center py-4">{t('form.not_found_product')}</div>
          </div>
        )}

        <div className="row g-3 pt-4">
          <div className="col-md-6 pb-2 text-end fw-semibold">{t('title.delivery_cost')}</div>
          <div className="col-md-6 pb-2">
            <InputCurrency
              id="delivery_fee"
              value={totalData.shippingCost}
              onChange={handleShippingChange}
              currency="IDR"
              readOnly={currentPage === 'view' ? true : false}
              zeroMinValue={true}
            />
            {currentPage !== 'view' && (
              <>
                <div className="before-value-text">
                  {t('form.correction_shipping_cost_before')}: Rp{' '}
                  {formatDecimal(cleanNumber(totalData.shippingCostBefore), 5)}
                </div>
              </>
            )}
            <div className="before-value-text text-success">
              {t('form.correction_shipping_cost_before_billed')}: Rp{' '}
              {formatDecimal(cleanNumber(totalData.shippingCostBilled), 5)}
            </div>
          </div>

          <div className="col-md-6 pb-2 text-end fw-semibold">
            {t('title.delivery_insurance_cost')}
          </div>
          <div className="col-md-6 pb-2">
            <InputCurrency
              id="delivery_insurance_cost"
              value={totalData.insuranceCost}
              onChange={handleInsurranceChange}
              currency="IDR"
              readOnly={currentPage === 'view' ? true : false}
              zeroMinValue={true}
            />
            {currentPage !== 'view' && (
              <>
                <div className="before-value-text">
                  {t('form.correction_insurance_cost_before')}: Rp
                  {formatDecimal(cleanNumber(totalData.insuranceCostBefore), 5)}
                </div>
              </>
            )}
            <div className="before-value-text text-success">
              {t('form.correction_insurance_cost_before_billed')}: Rp
              {formatDecimal(cleanNumber(totalData.insuranceCostBilled), 5)}
            </div>
          </div>

          <div className="col-md-6 pb-2 text-end fw-semibold">{t('title.total_transaction')}</div>
          <div className="col-md-6 pb-2">
            <InputCurrency
              id="total_order"
              value={totalData.totalOrder}
              decimal={2}
              readOnly
              currency="IDR"
            />
          </div>

          <div className="col-md-6 text-end fw-semibold">{t('title.payment_total')}</div>
          <div className="col-md-6">
            <InputCurrency
              id="total_payment"
              value={totalData.totalPayment}
              decimal={2}
              readOnly
              currency="IDR"
            />
          </div>
          {currentPage !== 'view' && orderData?.bill && (
            <div className="col-md-12 pt-4 px-5">
              <div className="row alert alert-warning align-items-center" role="alert">
                <div className="col-md-6">
                  <div>
                    <b>{t('title.invoice_total')}</b>
                  </div>
                  <div>{t('title.invoice_paid_issued')}</div>
                </div>
                <div className="col-md-6">
                  <div>
                    <b>{currencyFormat(totalAlreadyPaidAndProcess(orderData?.bill), 'IDR')}</b>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <OrderCorrectionProductModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onResetPagination={handleResetPagination}
        productList={productList}
        onToggle={handleProduct}
        onPageChange={handleChangePage}
        pagination={pagination}
        items={items}
        handleSubmitSearch={handleSubmitSearch}
        setKeyword={setKeyword}
        keyword={keyword}
        handleReset={handleReset}
      />
    </div>
  )
}

OrderCorrectionItemsSection.propTypes = {
  label: PropTypes.string.isRequired,
  orderData: PropTypes.object,
  items: PropTypes.array.isRequired,
  setItems: PropTypes.func.isRequired,
  totalData: PropTypes.object.isRequired,
  setTotalData: PropTypes.func.isRequired,
  totalAlreadyPaidAndProcess: PropTypes.func,
  setIsAllocated: PropTypes.func,
  finalAllocatedValue: PropTypes.any,
  isAllocatedAmount: PropTypes.bool,
  setIsAllocatedAmount: PropTypes.func,
}

export default OrderCorrectionItemsSection
